/** MF KYC — what NSE holds for a client, and starting eKYC when it holds nothing. */
import { useCallback, useMemo, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { KYC_ONBOARDING_UPDATE, MF_CHECK_KYC, MF_KYC_FRESH_REGISTER } from '../mutations';
import { KYC_ENUM_OPTIONS, KYC_ONBOARDING } from '../queries';

/** The onboarding steps in order — the progress this screen reports against. */
export const ONBOARDING_STEPS = [
  'INITIATED', 'PERSONAL_DETAILS', 'DECLARATIONS', 'IDENTITY_VERIFICATION',
  'BANK_DETAILS', 'CONSENTS', 'NOMINEE_DETAILS', 'DOCUMENT_SIGNING', 'SUBMITTED',
];

/** "L5_TO_10L" → "₹5L – ₹10L", and anything unmapped into plain words. */
const ENUM_LABELS = {
  BELOW_1L: 'Below ₹1L', L1_TO_5L: '₹1L – ₹5L', L5_TO_10L: '₹5L – ₹10L',
  L10_TO_25L: '₹10L – ₹25L', L25_TO_1CR: '₹25L – ₹1Cr', ABOVE_1CR: 'Above ₹1Cr',
  NONE: 'None', LESS_THAN_1_YEAR: 'Under a year', ONE_TO_3_YEARS: '1 – 3 years',
  THREE_TO_5_YEARS: '3 – 5 years', MORE_THAN_5_YEARS: 'Over 5 years',
};
export const kycEnumLabel = (v) => (v == null || v === ''
  ? '—'
  : ENUM_LABELS[v] || String(v).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()));

/**
 * The option lists the form offers.
 *
 * Normalised to one shape — `{ value, label }` — because the backend answers
 * two: plain strings for income, experience, risk and account type, and
 * `CodeOption` objects for states, countries and the rest.
 */
export function useKycEnumOptions({ skip = false } = {}) {
  const { data, loading, error } = useQuery(KYC_ENUM_OPTIONS, {
    skip, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });

  return useMemo(() => {
    const o = data?.getKycOnboardingEnumOptions ?? null;
    const plain = (list) => (list ?? []).map((v) => ({ value: v, label: kycEnumLabel(v) }));
    const coded = (list) => (list ?? []).map((c) => ({ value: c.value, label: c.label || kycEnumLabel(c.value) }));
    return {
      loading,
      error,
      incomeRanges: plain(o?.incomeRanges),
      tradingExperiences: plain(o?.tradingExperiences),
      riskProfiles: plain(o?.riskProfiles),
      bankAccountTypes: plain(o?.bankAccountTypes),
      states: coded(o?.states),
      countries: coded(o?.countries),
      communicationModes: coded(o?.communicationModes),
      dividendModes: coded(o?.dividendModes),
      nomineeRelationships: coded(o?.nomineeRelationships),
      nomineeIdentityTypes: coded(o?.nomineeIdentityTypes),
      guardianRelationships: coded(o?.guardianRelationships),
    };
  }, [data, loading, error]);
}

/**
 * The read groups the onboarding record; the write takes it flat. One place
 * knows that, so neither the form nor the mutation has to.
 */
export const flattenOnboarding = (data) => ({
  ...(data?.personalDetails ?? {}),
  ...(data?.bankDetails ?? {}),
  ...(data?.declarations ?? {}),
});

/**
 * Completing or correcting a client's KYC from the distributor's side.
 *
 * Saved SECTION BY SECTION, which is not a style choice: the backend works out
 * which onboarding step a payload belongs to from the fields it contains, so a
 * partial payload — one toggled boolean, say — can be attributed to the wrong
 * step and leave the rest of that step unset. Each save therefore carries every
 * answered field of its own section.
 *
 * `submit: true` is a separate, final act: it files the record and activates
 * the account, so it is never bundled into an ordinary section save.
 *
 * `save` resolves with the server's answer, or `{ success: false, message }`
 * when the request itself failed — never null, so a caller (the agent's
 * handlers) has the reason in hand rather than in the next render's `error`.
 * `awaitRefetch: true` holds the promise until the record has been read back,
 * so `readRecord` then answers with what the server now holds (the agent's
 * report names the step the save moved the record to).
 */
export function useKycOnboardingUpdate() {
  const apollo = useApolloClient();
  const [mutate, { loading: saving }] = useMutation(KYC_ONBOARDING_UPDATE);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const save = useCallback(async (userId, fields, { submit = false, awaitRefetch = false } = {}) => {
    setError(null);
    try {
      const { data } = await mutate({
        /* `submit` only travels when finalising — sending `submit: false` on
           every section save would add a field to each payload the step
           detection has to read past. */
        variables: { data: submit ? { ...fields, submit: true } : fields, userId: Number(userId) },
        refetchQueries: [{ query: KYC_ONBOARDING, variables: { userId: Number(userId) } }],
        awaitRefetchQueries: awaitRefetch,
      });
      const r = data?.kycOnboarding ?? null;
      setResult(r);
      if (r && !r.success) setError(r.message || 'Could not save the KYC record');
      return r ?? { success: false, message: 'the server did not answer' };
    } catch (err) {
      setError(err.message);
      return { success: false, message: err.message };
    }
  }, [mutate]);

  /* The onboarding record as the cache holds it — no request. Null when it was
     never read, or the cache cannot answer the whole query. */
  const readRecord = useCallback((userId) => {
    try {
      return apollo.readQuery({ query: KYC_ONBOARDING, variables: { userId: Number(userId) } })?.getKycOnboarding ?? null;
    } catch {
      return null;
    }
  }, [apollo]);

  return {
    save, readRecord, saving, error, result, reset: () => { setError(null); setResult(null); },
  };
}

/**
 * NSE's KYC check, read the way the backend's own onboarding gate reads it.
 *
 * `kycStatus` is NSE's bare code ("S") and says nothing a pattern over words
 * can match; the verdict is the remark ("07 - KYC Validated"), matched as a
 * lowercase substring because NSE prefixes and cases it inconsistently. The
 * markers are the backend's (`KYC_VALIDATED_MARKER`, `KYC_REJECTED_MARKERS`,
 * `KYC_SERVICE_DOWN_MARKER` in `db/models/mf_enums.py`). Reading the code
 * instead called every validated client "not complete" and offered to email
 * them an eKYC link.
 *
 * - `done`: validated.
 * - `rejected`: rejected or deactivated at the KRA.
 * - `unreadable`: the registry was down — not a verdict on the PAN, and never a
 *   reason to start eKYC.
 * - `pending`: anything else NSE answered with ("KYC REGISTERED", "ON HOLD",
 *   "UNDER_PROCESS", …), which the backend also treats as not there yet.
 * - `unknown`: no answer to read.
 */
export function kycVerdict(result) {
  if (!result) return 'unknown';
  const remark = String(result.kycStatusRemark || '').toLowerCase();
  if (remark.includes('service_down')) return 'unreadable';
  if (remark.includes('validated')) return 'done';
  if (remark.includes('rejected') || remark.includes('deactivate')) return 'rejected';
  if (remark || result.kycStatus) return 'pending';
  return 'unknown';
}

/** A `kycVerdict` → the tone its chip is read in. */
export const KYC_VERDICT_TONE = Object.freeze({
  done: 'green', rejected: 'red', pending: 'amber', unreadable: 'grey', unknown: 'grey',
});

/** A stored status word read as done. For words on file; NSE's check is read with `kycVerdict`. */
export const isKycDone = (status) => /VERIFIED|SUCCESS|COMPLETE|APPROVED/i.test(String(status || ''));

/** Stored status word → the tone it should be read in. */
export const kycTone = (status) => {
  const s = String(status || '').toUpperCase();
  if (/VERIFIED|SUCCESS|COMPLETE|APPROVED|ACTIVE/.test(s)) return 'green';
  if (/REJECT|FAIL|ERROR/.test(s)) return 'red';
  if (/PENDING|PROGRESS|SUBMITTED|UPLOADED/.test(s)) return 'amber';
  return 'grey';
};

/**
 * One client's KYC, checked on demand.
 *
 * `check` and `startEkyc` resolve with NSE's answer, or `{ success: false,
 * message }` when the request itself failed — never null, so a caller (the
 * agent's handlers) has the reason in hand rather than in the next render.
 *
 * Held as component state rather than in the cache because neither call is a
 * read of stored data: the check asks NSE now, and the register starts a flow
 * whose link is only meaningful for the client it was minted for. Switching
 * client resets both — a verdict left on screen under a different name is
 * worse than an empty panel.
 *
 * Both are also stamped with the client they answer for (`result.userId`,
 * `link.userId`), because a reset cannot catch an answer that lands after it:
 * a check still in flight, or an eKYC the agent started from a confirmation
 * card the reader answered after opening another client. The page shows them
 * only under that client.
 */
export function useMfKyc() {
  const [checkMutation, { loading: checking }] = useMutation(MF_CHECK_KYC);
  const [registerMutation, { loading: starting }] = useMutation(MF_KYC_FRESH_REGISTER);

  const [result, setResult] = useState(null);
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setResult(null);
    setLink(null);
    setError(null);
  }, []);

  const check = useCallback(async (userId) => {
    setError(null);
    try {
      const { data } = await checkMutation({ variables: { userId: Number(userId) } });
      const r = data?.mfCheckKyc ?? null;
      setResult(r ? { ...r, userId: String(userId) } : null);
      if (r && !r.success && r.message) setError(r.message);
      return r;
    } catch (err) {
      setError(err.message);
      /* Answered in NSE's own shape, so a caller reads one kind of failure. */
      return { success: false, message: err.message };
    }
  }, [checkMutation]);

  const startEkyc = useCallback(async (email, userId) => {
    setError(null);
    try {
      const { data } = await registerMutation({ variables: { email, userId: Number(userId) } });
      const r = data?.mfKycFreshRegister ?? null;
      if (!r?.success) {
        setError(r?.message || 'Could not start eKYC');
        return r;
      }
      setLink(r.link ? { userId: String(userId), url: r.link } : null);
      return r;
    } catch (err) {
      setError(err.message);
      return { success: false, message: err.message };
    }
  }, [registerMutation]);

  return {
    check, startEkyc, reset, result, link, error,
    checking, starting, busy: checking || starting,
  };
}
